// frontend/src/features/forms/pages/FormRevisionEditorPage.tsx
// AM-006: بازنویسی کامل برای پشتیبانی از header/body/footer، repeater، chain_rules، binding
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  useV1FormsFormRevisionsRetrieve,
  useV1FormsFormRevisionsPartialUpdate,
  useV1FormsFormRevisionsPublishCreate,
} from "@/api/generated/v1/v1";
import { useIsDeploymentPhaseActive } from '@/features/tenant-admin/api/deploymentPhaseApi';
import {
  buildJsonSchema,
  validateDefinitionKeys,
  emptyDefinition,
  type FormFieldDef,
  type FormFieldType,
  type FormSection,
  type FormDefinitionJson,
  type ChainRule,
} from "@/shared/utils/formSchema";
import { DynamicFormRenderer } from "../components/DynamicFormRenderer";
import { PageBanner } from "@/shared/components/PageBanner";

const FIELD_TYPES: { value: FormFieldType; label: string }[] = [
  { value: "text", label: "متن کوتاه" },
  { value: "textarea", label: "متن بلند" },
  { value: "number", label: "عدد" },
  { value: "select", label: "لیست انتخابی" },
  { value: "checkbox", label: "بله/خیر" },
  { value: "date", label: "تاریخ" },
  { value: "repeater", label: "جدول تکرارشونده" },
];

const OPERATORS: { value: ChainRule["operator"]; label: string }[] = [
  { value: "eq", label: "مساوی" },
  { value: "lt", label: "کمتر از" },
  { value: "lte", label: "کمتر یا مساوی" },
  { value: "gt", label: "بیشتر از" },
  { value: "gte", label: "بیشتر یا مساوی" },
  { value: "between", label: "بین دو مقدار" },
  { value: "in", label: "یکی از (برای لیست)" },
];

type SectionKey = "header" | "body" | "footer";

export function FormRevisionEditorPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: revision, isLoading } = useV1FormsFormRevisionsRetrieve(id!);
  const { mutateAsync: updateRevision, isPending: isSaving } = useV1FormsFormRevisionsPartialUpdate();
  const { mutateAsync: publishRevision, isPending: isPublishing } = useV1FormsFormRevisionsPublishCreate();

  const [def, setDef] = useState<FormDefinitionJson>(emptyDefinition(""));
  const [preview, setPreview] = useState<Record<string, any>>({});
  const [errorMsg, setErrorMsg] = useState("");
  const [allowedModelsText, setAllowedModelsText] = useState("");
  const [inheritText, setInheritText] = useState("");

  useEffect(() => {
    if (revision?.definition_json) {
      const d: any = revision.definition_json;
      // سازگاری با دادهٔ قدیمی (قبل از AM-006) که فقط fields تخت داشت
      const normalized: FormDefinitionJson = {
        title: d.title || "",
        layout: d.layout || { columns: 1 },
        header: d.header || { fields: [] },
        body: d.body || { fields: d.fields || [] },
        footer: d.footer || { fields: [], signature_required: false },
        binding: d.binding || { required: false, allowed_models: [] },
        inherit_from_parent: d.inherit_from_parent || [],
        schema: d.schema || {},
      };
      setDef(normalized);
      setAllowedModelsText((normalized.binding?.allowed_models || []).join(","));
      setInheritText((normalized.inherit_from_parent || []).join(","));
    }
  }, [revision]);

  const { data: isDeploymentPhase = false } = useIsDeploymentPhaseActive();
  const isReadOnly = revision?.status !== "draft" && !isDeploymentPhase;

  const updateSection = (key: SectionKey, updater: (s: FormSection) => FormSection) => {
    setDef((prev) => ({ ...prev, [key]: updater(prev[key]) }));
  };

  const addField = (key: SectionKey) => {
    updateSection(key, (s) => ({
      ...s,
      fields: [...s.fields, { key: "", label: "", type: "text", required: false }],
    }));
  };

  const updateField = (key: SectionKey, idx: number, patch: Partial<FormFieldDef>) => {
    updateSection(key, (s) => ({
      ...s,
      fields: s.fields.map((f, i) => (i === idx ? { ...f, ...patch } : f)),
    }));
  };

  const removeField = (key: SectionKey, idx: number) => {
    updateSection(key, (s) => ({ ...s, fields: s.fields.filter((_, i) => i !== idx) }));
  };

  const addColumn = (key: SectionKey, fieldIdx: number) => {
    updateSection(key, (s) => ({
      ...s,
      fields: s.fields.map((f, i) =>
        i === fieldIdx
          ? { ...f, columns: [...(f.columns || []), { key: "", label: "", type: "text", required: false }] }
          : f
      ),
    }));
  };

  const updateColumn = (key: SectionKey, fieldIdx: number, colIdx: number, patch: Partial<FormFieldDef>) => {
    updateSection(key, (s) => ({
      ...s,
      fields: s.fields.map((f, i) =>
        i === fieldIdx
          ? { ...f, columns: (f.columns || []).map((c, ci) => (ci === colIdx ? { ...c, ...patch } : c)) }
          : f
      ),
    }));
  };

  const removeColumn = (key: SectionKey, fieldIdx: number, colIdx: number) => {
    updateSection(key, (s) => ({
      ...s,
      fields: s.fields.map((f, i) =>
        i === fieldIdx ? { ...f, columns: (f.columns || []).filter((_, ci) => ci !== colIdx) } : f
      ),
    }));
  };

  const addChainRule = (key: SectionKey, fieldIdx: number) => {
    updateSection(key, (s) => ({
      ...s,
      fields: s.fields.map((f, i) =>
        i === fieldIdx
          ? { ...f, chain_rules: [...(f.chain_rules || []), { operator: "eq", value: 0, target_family_code: "" }] }
          : f
      ),
    }));
  };

  const updateChainRule = (key: SectionKey, fieldIdx: number, ruleIdx: number, patch: Partial<ChainRule>) => {
    updateSection(key, (s) => ({
      ...s,
      fields: s.fields.map((f, i) =>
        i === fieldIdx
          ? {
            ...f,
            chain_rules: (f.chain_rules || []).map((r, ri) => (ri === ruleIdx ? { ...r, ...patch } : r)),
          }
          : f
      ),
    }));
  };

  const removeChainRule = (key: SectionKey, fieldIdx: number, ruleIdx: number) => {
    updateSection(key, (s) => ({
      ...s,
      fields: s.fields.map((f, i) =>
        i === fieldIdx ? { ...f, chain_rules: (f.chain_rules || []).filter((_, ri) => ri !== ruleIdx) } : f
      ),
    }));
  };

  const buildFinalDefinition = (): FormDefinitionJson => ({
    ...def,
    binding: {
      required: def.binding?.required || false,
      allowed_models: allowedModelsText.split(",").map((s) => s.trim()).filter(Boolean),
    },
    inherit_from_parent: inheritText.split(",").map((s) => s.trim()).filter(Boolean),
    schema: buildJsonSchema(def.header, def.body, def.footer),
  });

  const handleSave = async () => {
    setErrorMsg("");
    const keyErr = validateDefinitionKeys(def);
    if (keyErr) {
      setErrorMsg(keyErr);
      return;
    }
    const definition_json = buildFinalDefinition();
    try {
      await updateRevision({ id: id!, data: { definition_json } as any });
      queryClient.invalidateQueries({ queryKey: ["v1FormsFormRevisionsRetrieve", id] });
      alert("ذخیره شد.");
    } catch (err: any) {
      setErrorMsg(err.response?.data?.detail || JSON.stringify(err.response?.data) || "خطا در ذخیرهٔ نسخه");
    }
  };

  const handlePublish = async () => {
    if (!confirm("بعد از انتشار، ساختار این نسخه غیرقابل تغییر می‌شود. ادامه می‌دهید؟")) return;
    try {
      await handleSave();
      await publishRevision({ id: id!, data: {} as any });
      queryClient.invalidateQueries({ queryKey: ["v1FormsFormRevisionsRetrieve", id] });
      navigate(-1);
    } catch (err: any) {
      setErrorMsg(err.response?.data?.detail || JSON.stringify(err.response?.data) || "خطا در انتشار نسخه (احتمالاً چرخهٔ Form Chaining پیدا شده)");
    }
  };

  if (isLoading) {
    return (
      <div className="p-8 flex-center">
        <p style={{ color: "var(--color-text-muted)" }}>در حال بارگذاری...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: "0.5rem 0", maxWidth: "100%", margin: "0", display: "flex", flexDirection: "column", gap: "1.5rem" }}>
      <PageBanner
        title={`ویرایش نسخهٔ ${revision?.revision_no || ""} ${isReadOnly ? "(فقط‌خواندنی — منتشرشده)" : ""}`}
        subtitle="ویرایش فیلدها، قوانین و ساختار فرم"
        icon="🛠️"
        helpLink="/help/forms/revision-editor"
      >
        <div style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}>
          {!isReadOnly && (
            <>
              <button
                onClick={handleSave}
                disabled={isSaving}
                style={{
                  padding: "0.6rem 1.25rem",
                  borderRadius: "12px",
                  background: "#ffffff",
                  color: "#007270",
                  fontWeight: 700,
                  fontSize: "0.9rem",
                  border: "none",
                  cursor: isSaving ? "not-allowed" : "pointer",
                  opacity: isSaving ? 0.7 : 1,
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  transition: "all 0.2s ease",
                }}
              >
                <span>💾</span>
                <span>{isSaving ? "..." : (isDeploymentPhase && revision?.status !== "draft" ? "ذخیره‌ی مستقیم" : "ذخیرهٔ پیش‌نویس")}</span>
              </button>
              <button
                onClick={handlePublish}
                disabled={isPublishing}
                style={{
                  padding: "0.6rem 1.25rem",
                  borderRadius: "12px",
                  background: "#BA9960",
                  color: "#ffffff",
                  fontWeight: 700,
                  fontSize: "0.9rem",
                  border: "none",
                  cursor: isPublishing ? "not-allowed" : "pointer",
                  opacity: isPublishing ? 0.7 : 1,
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  transition: "all 0.2s ease",
                }}
              >
                <span>🚀</span>
                <span>{isPublishing ? "..." : "انتشار نسخه"}</span>
              </button>
            </>
          )}
          <button
            onClick={() => navigate(-1)}
            style={{
              padding: "0.6rem 1rem",
              borderRadius: "12px",
              background: "rgba(255, 255, 255, 0.15)",
              color: "#ffffff",
              border: "1px solid rgba(255, 255, 255, 0.3)",
              fontWeight: 700,
              fontSize: "0.85rem",
              cursor: "pointer",
            }}
          >
            ← بازگشت
          </button>
        </div>
      </PageBanner>

      <div className="brand-card-extand" style={{ maxWidth: "1100px", margin: "0 auto", padding: "2rem", width: "100%" }}>

        {errorMsg && (
          <div className="p-4 mb-4" style={{ background: "rgba(255,0,0,0.1)", borderLeft: "4px solid red", borderRadius: "8px" }}>
            <p style={{ color: "red" }}>{errorMsg}</p>
          </div>
        )}

        {isDeploymentPhase && revision?.status !== "draft" && (
          <div className="p-4 mb-4" style={{ background: "rgba(234, 179, 8, 0.08)", borderLeft: "4px solid #eab308", borderRadius: "8px" }}>
            <p style={{ color: "#854d0e", fontWeight: "bold" }}>⚠️ این نسخه منتشرشده است؛ تغییرات مستقیماً روی نسخه‌ی زنده اعمال می‌شود.</p>
          </div>
        )}

        {/* تنظیمات کلی */}
        <div className="brand-card mb-6" style={{ padding: "1.5rem" }}>
          <h2 className="font-bold mb-4">تنظیمات کلی</h2>
          <div className="form-group mb-4">
            <label className="form-label">عنوان فرم</label>
            <input className="input-field" value={def.title} disabled={isReadOnly}
              onChange={(e) => setDef({ ...def, title: e.target.value })} />
          </div>
          <div className="form-group mb-4">
            <label className="form-label">چیدمان</label>
            <select className="input-field" dir="rtl" value={def.layout.columns} disabled={isReadOnly}
              onChange={(e) => setDef({ ...def, layout: { columns: Number(e.target.value) as 1 | 2 } })}>
              <option value={1}>یک ستون</option>
              <option value={2}>دو ستون</option>
            </select>
          </div>

          <h3 className="font-bold mt-6 mb-2" style={{ fontSize: "0.95rem" }}>اتصال به موجودیت (Entity Binding)</h3>
          <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.75rem" }}>
            <input type="checkbox" disabled={isReadOnly}
              checked={def.binding?.required || false}
              onChange={(e) => setDef({ ...def, binding: { ...def.binding!, required: e.target.checked } })} />
            این فرم اجباراً باید به یک موجودیت وصل شود
          </label>
          <div className="form-group">
            <label className="form-label">موجودیتهای مجاز (با کاما، مثال: inventory.inventorylot,production.productionorder)</label>
            <input className="input-field" dir="ltr" disabled={isReadOnly}
              value={allowedModelsText} onChange={(e) => setAllowedModelsText(e.target.value)} />
          </div>

          <h3 className="font-bold mt-6 mb-2" style={{ fontSize: "0.95rem" }}>ارثبری هدر از فرم والد (Form Chaining)</h3>
          <div className="form-group">
            <label className="form-label">key هایی که باید خودکار از فرم مبدأ کپی شوند (با کاما)</label>
            <input className="input-field" dir="ltr" disabled={isReadOnly}
              value={inheritText} onChange={(e) => setInheritText(e.target.value)}
              placeholder="product_code,production_order_no" />
          </div>
        </div>

        {(["header", "body", "footer"] as SectionKey[]).map((sectionKey) => (
          <SectionEditor
            key={sectionKey}
            sectionKey={sectionKey}
            title={sectionKey === "header" ? "هدر فرم" : sectionKey === "body" ? "بدنهٔ فرم" : "فوتر فرم"}
            section={def[sectionKey]}
            isReadOnly={isReadOnly}
            onAddField={() => addField(sectionKey)}
            onUpdateField={(idx, patch) => updateField(sectionKey, idx, patch)}
            onRemoveField={(idx) => removeField(sectionKey, idx)}
            onAddColumn={(fieldIdx) => addColumn(sectionKey, fieldIdx)}
            onUpdateColumn={(fieldIdx, colIdx, patch) => updateColumn(sectionKey, fieldIdx, colIdx, patch)}
            onRemoveColumn={(fieldIdx, colIdx) => removeColumn(sectionKey, fieldIdx, colIdx)}
            onAddChainRule={(fieldIdx) => addChainRule(sectionKey, fieldIdx)}
            onUpdateChainRule={(fieldIdx, ruleIdx, patch) => updateChainRule(sectionKey, fieldIdx, ruleIdx, patch)}
            onRemoveChainRule={(fieldIdx, ruleIdx) => removeChainRule(sectionKey, fieldIdx, ruleIdx)}
          />
        ))}

        {sectionKey_footer_extra(def, setDef, isReadOnly)}

        <h2 className="font-bold mt-8 mb-4">پیشنمایش فرم</h2>
        <div className="brand-card" style={{ padding: "1.5rem" }}>
          <DynamicFormRenderer definition={buildFinalDefinition()} value={preview}
            onChange={(key, val) => setPreview((p) => ({ ...p, [key]: val }))} />
        </div>
      </div>
    </div>
  );
}

function sectionKey_footer_extra(
  def: FormDefinitionJson,
  setDef: React.Dispatch<React.SetStateAction<FormDefinitionJson>>,
  isReadOnly: boolean
) {
  return (
    <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", margin: "0.5rem 0 2rem" }}>
      <input type="checkbox" disabled={isReadOnly}
        checked={def.footer.signature_required || false}
        onChange={(e) => setDef({ ...def, footer: { ...def.footer, signature_required: e.target.checked } })} />
      این فرم نیاز به امضای دیجیتال دارد
    </label>
  );
}

interface SectionEditorProps {
  sectionKey: SectionKey;
  title: string;
  section: FormSection;
  isReadOnly?: boolean;
  onAddField: () => void;
  onUpdateField: (idx: number, patch: Partial<FormFieldDef>) => void;
  onRemoveField: (idx: number) => void;
  onAddColumn: (fieldIdx: number) => void;
  onUpdateColumn: (fieldIdx: number, colIdx: number, patch: Partial<FormFieldDef>) => void;
  onRemoveColumn: (fieldIdx: number, colIdx: number) => void;
  onAddChainRule: (fieldIdx: number) => void;
  onUpdateChainRule: (fieldIdx: number, ruleIdx: number, patch: Partial<ChainRule>) => void;
  onRemoveChainRule: (fieldIdx: number, ruleIdx: number) => void;
}

function SectionEditor({
  title, section, isReadOnly, onAddField, onUpdateField, onRemoveField,
  onAddColumn, onUpdateColumn, onRemoveColumn,
  onAddChainRule, onUpdateChainRule, onRemoveChainRule,
}: SectionEditorProps) {
  return (
    <div className="brand-card mb-6" style={{ padding: "1.5rem" }}>
      <h2 className="font-bold mb-4">{title}</h2>
      <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
        {section.fields.map((f, idx) => (
          <div key={idx} className="brand-card" style={{ padding: "1rem", background: "rgba(255,255,255,0.02)" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr auto auto", gap: "0.75rem", alignItems: "center" }}>
              <input className="input-field" placeholder="key (لاتین، snake_case)" dir="ltr" disabled={isReadOnly}
                value={f.key} onChange={(e) => onUpdateField(idx, { key: e.target.value })} />
              <input className="input-field" placeholder="برچسب فارسی" disabled={isReadOnly}
                value={f.label} onChange={(e) => onUpdateField(idx, { label: e.target.value })} />
              <select className="input-field" dir="rtl" disabled={isReadOnly}
                value={f.type} onChange={(e) => onUpdateField(idx, { type: e.target.value as FormFieldType })}>
                {FIELD_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
              <label style={{ display: "flex", alignItems: "center", gap: "0.4rem", fontSize: "0.85rem" }}>
                <input type="checkbox" disabled={isReadOnly} checked={f.required}
                  onChange={(e) => onUpdateField(idx, { required: e.target.checked })} />
                اجباری
              </label>
              {!isReadOnly && (
                <button className="btn btn-secondary" style={{ fontSize: "0.8rem" }} onClick={() => onRemoveField(idx)}>حذف</button>
              )}
            </div>

            {f.type === "select" && (
              <div className="mt-3">
                <input className="input-field" placeholder="گزینهها: ok:سالم,nok:مردود" disabled={isReadOnly}
                  value={(f.options || []).map((o) => `${o.value}:${o.label}`).join(",")}
                  onChange={(e) => {
                    const options = e.target.value.split(",").map((p) => p.split(":"))
                      .filter((p): p is [string, string] => p.length === 2 && typeof p[0] === "string" && typeof p[1] === "string")
                      .map(([value, label]) => ({ value: value.trim(), label: label.trim() }));
                    onUpdateField(idx, { options });
                  }} />
              </div>
            )}

            {f.type === "repeater" && (
              <div className="mt-4" style={{ borderTop: "1px dashed var(--glass-border)", paddingTop: "1rem" }}>
                <div className="flex-between mb-2">
                  <span style={{ fontSize: "0.85rem", color: "var(--color-text-muted)" }}>ستونهای جدول تکرارشونده</span>
                  {!isReadOnly && (
                    <button className="btn btn-secondary" style={{ fontSize: "0.75rem" }} onClick={() => onAddColumn(idx)}>+ ستون</button>
                  )}
                </div>
                {(f.columns || []).map((col, ci) => (
                  <div key={ci} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr auto", gap: "0.5rem", marginBottom: "0.5rem" }}>
                    <input className="input-field" placeholder="key" dir="ltr" disabled={isReadOnly}
                      value={col.key} onChange={(e) => onUpdateColumn(idx, ci, { key: e.target.value })} />
                    <input className="input-field" placeholder="برچسب" disabled={isReadOnly}
                      value={col.label} onChange={(e) => onUpdateColumn(idx, ci, { label: e.target.value })} />
                    <select className="input-field" dir="rtl" disabled={isReadOnly}
                      value={col.type} onChange={(e) => onUpdateColumn(idx, ci, { type: e.target.value as FormFieldType })}>
                      {FIELD_TYPES.filter((t) => t.value !== "repeater").map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                    {!isReadOnly && (
                      <button className="btn btn-secondary" style={{ fontSize: "0.75rem" }} onClick={() => onRemoveColumn(idx, ci)}>حذف</button>
                    )}
                  </div>
                ))}
                <div className="form-group mt-2" style={{ maxWidth: "200px" }}>
                  <label className="form-label" style={{ fontSize: "0.8rem" }}>حداقل ردیف</label>
                  <input type="number" className="input-field" disabled={isReadOnly}
                    value={f.min_rows ?? 0} onChange={(e) => onUpdateField(idx, { min_rows: Number(e.target.value) })} />
                </div>
              </div>
            )}

            {(f.type === "number" || f.type === "select") && (
              <div className="mt-4" style={{ borderTop: "1px dashed var(--glass-border)", paddingTop: "1rem" }}>
                <div className="flex-between mb-2">
                  <span style={{ fontSize: "0.85rem", color: "var(--color-text-muted)" }}>
                    اگر-آنگاه (Form Chaining) — این فیلد کدام فرم بعدی را باز میکند
                  </span>
                  {!isReadOnly && (
                    <button className="btn btn-secondary" style={{ fontSize: "0.75rem" }} onClick={() => onAddChainRule(idx)}>+ قانون</button>
                  )}
                </div>
                {(f.chain_rules || []).map((rule, ri) => (
                  <div key={ri} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr auto", gap: "0.5rem", marginBottom: "0.5rem" }}>
                    <select className="input-field" dir="rtl" disabled={isReadOnly}
                      value={rule.operator} onChange={(e) => onUpdateChainRule(idx, ri, { operator: e.target.value as ChainRule["operator"] })}>
                      {OPERATORS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                    <input className="input-field" placeholder='مقدار (برای "بین" مثل 20,30)' dir="ltr" disabled={isReadOnly}
                      value={Array.isArray(rule.value) ? rule.value.join(",") : String(rule.value)}
                      onChange={(e) => {
                        const raw = e.target.value;
                        const val = rule.operator === "between" || rule.operator === "in"
                          ? raw.split(",").map((v) => v.trim())
                          : (isNaN(Number(raw)) ? raw : Number(raw));
                        onUpdateChainRule(idx, ri, { value: val as any });
                      }} />
                    <input className="input-field" placeholder="کد فرم مقصد (مثال: QC-LENGTH-SHORT)" dir="ltr" disabled={isReadOnly}
                      value={rule.target_family_code} onChange={(e) => onUpdateChainRule(idx, ri, { target_family_code: e.target.value })} />
                    {!isReadOnly && (
                      <button className="btn btn-secondary" style={{ fontSize: "0.75rem" }} onClick={() => onRemoveChainRule(idx, ri)}>حذف</button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
        {!isReadOnly && (
          <button className="btn btn-secondary" onClick={onAddField}>+ افزودن فیلد به {title}</button>
        )}
      </div>
    </div>
  );
}
